/**
 * Gerüst: Anmeldezustand, Navigation, Kopfzeile, Hinweis-Einblendungen.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { dayKey, de, dowLabel, fmtClock, fullDateLabel, longDateLabel } from '@zeit/domain';
import { clockOffset, hasRelevantSkew } from './lib/clock.ts';
import { useNow, useRoute, useSession } from './lib/session.ts';
import { Loading, Toast } from './ui/index.tsx';
import { Login } from './views/Login.tsx';
import { Terminal } from './views/Terminal.tsx';
import { Week } from './views/Week.tsx';
import { Export } from './views/Export.tsx';
import { Admin } from './views/Admin.tsx';

type Notice = { text: string; tone?: 'error' };

const TABS = [
  { route: 'heute', label: de.nav.today },
  { route: 'woche', label: de.nav.week },
  { route: 'export', label: de.nav.export },
] as const;

type Tab = { route: string; label: string };

export function App() {
  const { state, reload, logout } = useSession();
  const [route, navigate] = useRoute('heute');
  const [notice, setNotice] = useState<Notice | null>(null);
  const timer = useRef<number>();

  const toast = useCallback((text: string, tone?: 'error') => {
    window.clearTimeout(timer.current);
    setNotice({ text, tone });
    timer.current = window.setTimeout(() => setNotice(null), tone === 'error' ? 5000 : 2400);
  }, []);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  if (state.status === 'loading') return <Loading />;
  if (state.status === 'anonymous') return <Login onSignedIn={reload} />;

  if (state.status === 'incomplete') {
    return (
      <div className="auth-wrap">
        <div className="auth-card">
          <div className="brand">{de.app.name}</div>
          <p style={{ marginTop: 16 }}>{de.auth.noProfile}</p>
          <button type="button" className="btn btn-secondary" onClick={logout}>
            {de.nav.logout}
          </button>
        </div>
      </div>
    );
  }

  const { session } = state;
  const tabs = session.isAdmin
    ? [...TABS, { route: 'verwaltung', label: de.nav.admin } as const]
    : TABS;
  const active = tabs.some((t) => t.route === route) ? route : 'heute';

  return (
    <div className="shell">
      <TopBar
        name={session.employee.name}
        tabs={tabs}
        active={active}
        onNavigate={navigate}
        onLogout={logout}
      />

      {active === 'heute' && <Terminal session={session} onToast={toast} />}
      {active === 'woche' && <Week session={session} onToast={toast} />}
      {active === 'export' && <Export session={session} onToast={toast} />}
      {active === 'verwaltung' && session.isAdmin && <Admin session={session} onToast={toast} />}

      {notice && <Toast text={notice.text} tone={notice.tone} />}
    </div>
  );
}

function TopBar({
  name,
  tabs,
  active,
  onNavigate,
  onLogout,
}: {
  name: string;
  tabs: readonly Tab[];
  active: string;
  onNavigate: (route: string) => void;
  onLogout: () => void;
}) {
  const now = useNow(true);
  const today = dayKey(now);

  return (
    <header className="topbar">
      <div className="topbar-top">
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, flexWrap: 'wrap', minWidth: 0 }}>
          <span className="brand">{de.app.name}</span>
          <span className="greeting">{de.app.greeting(firstName(name))}</span>
        </div>

        {/*
          Das Datum steht auf der Höhe von Marke und Begrüssung, mittig —
          also genau über der Uhr, mit der es zusammengehört.
        */}
        <span className="kicker topbar-date-long">{longDateLabel(today)}</span>

        <AccountMenu onNavigate={onNavigate} onLogout={onLogout} />
      </div>

      <div className="topbar-bottom">
        <MainMenu tabs={tabs} active={active} onNavigate={onNavigate} />

        <div className="topbar-clock">
          {/*
            Auf dem Telefon bleibt das Datum bei der Uhr und in der kurzen
            Form: oben nebeneinander wäre für Marke, Begrüssung, Datum und
            Zahnrad kein Platz, und „Donnerstag, 24. September 2026" bräuchte
            dort zwei Zeilen.
          */}
          <span className="kicker topbar-date-short">
            {dowLabel(today)}, {fullDateLabel(today)}
          </span>
          <span className="topbar-time num">{fmtClock(clockOfDay(now))}</span>
        </div>

        <div className="topbar-trailing">
          <ClockSkewHint />
        </div>
      </div>
    </header>
  );
}

/**
 * Der Rufname aus dem hinterlegten Namen.
 *
 * Begrüsst wird mit dem ersten Namensteil — „Hallo Moritz!" statt „Hallo
 * Moritz Beck!". Besteht der Name aus einem Teil, bleibt er unverändert.
 */
function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] || name;
}

/**
 * Ein Klappmenü im Kopfband.
 *
 * Es gibt zwei davon — Navigation links, Konto rechts — und sie verhalten
 * sich gleich: schliessen bei Auswahl, bei Klick daneben und mit Escape, und
 * geben den Fokus dann an ihre Schaltfläche zurück. Die Mechanik steht
 * deshalb einmal hier und nicht zweimal in zwei fast gleichen Komponenten.
 *
 * `children` bekommt das Schliessen als Argument, damit jeder Eintrag nach
 * seinem eigenen Handgriff zumachen kann.
 */
function Dropdown({
  id,
  label,
  title,
  className,
  align = 'left',
  children,
}: {
  id: string;
  label: ReactNode;
  title: string;
  className: string;
  align?: 'left' | 'right';
  children: (close: () => void) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent) => {
      if (!box.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      trigger.current?.focus();
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div className="menu" ref={box}>
      <button
        type="button"
        ref={trigger}
        className={className}
        aria-expanded={open}
        aria-controls={id}
        aria-haspopup="true"
        aria-label={title}
        onClick={() => setOpen((v) => !v)}
      >
        {label}
      </button>

      <nav
        id={id}
        className={`menu-panel${align === 'right' ? ' menu-panel-right' : ''}`}
        aria-label={title}
        hidden={!open}
      >
        {children(() => setOpen(false))}
      </nav>
    </div>
  );
}

/**
 * Die Navigation, unter der Marke.
 *
 * Die Schaltfläche trägt den Namen des offenen Bereichs, damit die Einordnung
 * nicht verloren geht, seit das Reiterband weg ist.
 */
function MainMenu({
  tabs,
  active,
  onNavigate,
}: {
  tabs: readonly Tab[];
  active: string;
  onNavigate: (route: string) => void;
}) {
  const current = tabs.find((tab) => tab.route === active);

  return (
    <Dropdown
      id="hauptmenue"
      className="burger"
      title={current ? `${de.nav.menu} — ${current.label}` : de.nav.menu}
      label={
        <>
          <span className="burger-bars" aria-hidden />
          <span className="burger-label">{current?.label ?? de.nav.menu}</span>
        </>
      }
    >
      {(close) =>
        tabs.map((tab) => (
          <button
            key={tab.route}
            type="button"
            className="menu-item"
            aria-current={active === tab.route ? 'page' : undefined}
            onClick={() => {
              onNavigate(tab.route);
              close();
            }}
          >
            {tab.label}
          </button>
        ))
      }
    </Dropdown>
  );
}

/**
 * Das Konto-Menü rechts.
 *
 * Abmelden gehört nicht in die Navigation — es wechselt keinen Bereich,
 * sondern beendet die Sitzung. Es steht deshalb hier, zusammen mit dem Weg
 * zum eigenen Profil, und ist als letzter Eintrag abgesetzt.
 */
function AccountMenu({
  onNavigate,
  onLogout,
}: {
  onNavigate: (route: string) => void;
  onLogout: () => void;
}) {
  return (
    <Dropdown
      id="kontomenue"
      className="account-btn"
      title={de.nav.account}
      align="right"
      label={
        <>
          <GearIcon />
          <span className="account-label">{de.nav.account}</span>
        </>
      }
    >
      {(close) => (
        <>
          <button
            type="button"
            className="menu-item"
            onClick={() => {
              onNavigate('export');
              close();
            }}
          >
            {de.nav.profile}
          </button>

          <button type="button" className="menu-item menu-item-last" onClick={onLogout}>
            {de.nav.logout}
          </button>
        </>
      )}
    </Dropdown>
  );
}

/**
 * Zahnrad, acht Zähne, im Bild gezeichnet statt als Datei geladen.
 *
 * Ein Versuch mit CSS-Masken sah aus wie eine Schneeflocke; ein SVG mit acht
 * gedrehten Rechtecken und einem Ring ist vorhersagbar und kostet keinen
 * zusätzlichen Netzaufruf.
 */
function GearIcon() {
  return (
    <svg
      className="account-icon"
      viewBox="-12 -12 24 24"
      width="15"
      height="15"
      aria-hidden="true"
      focusable="false"
    >
      <g fill="currentColor">
        {[0, 45, 90, 135, 180, 225, 270, 315].map((angle) => (
          <rect key={angle} x={-1.7} y={-11.2} width={3.4} height={4.8} transform={`rotate(${angle})`} />
        ))}
      </g>
      <circle r="6.2" fill="none" stroke="currentColor" strokeWidth="2.8" />
    </svg>
  );
}

/**
 * Weist darauf hin, dass die Uhr dieses Geräts falsch geht.
 *
 * Angezeigt und gebucht wird ohnehin die Serverzeit, die Anwendung rechnet
 * also richtig. Wer aber auf die Uhr seines Rechners schaut und sich wundert,
 * warum der Stempel abweicht, soll den Grund sehen statt einen Fehler zu
 * vermuten.
 */
function ClockSkewHint() {
  if (!hasRelevantSkew()) return null;
  const minutes = Math.round(Math.abs(clockOffset()) / 60000);
  const direction = clockOffset() < 0 ? 'vor' : 'nach';
  return (
    <span
      className="tag tag-open"
      title={`Gebucht wird die Serverzeit. Die Uhr dieses Geräts geht rund ${minutes} Minuten ${direction}.`}
    >
      Geräteuhr {minutes} Min. {direction}
    </span>
  );
}

/**
 * Sekunden seit Mitternacht in Berlin — die Wanduhr der Kopfzeile.
 *
 * Nicht `now % 86400000`: das wäre UTC-Mitternacht und ginge im Sommer zwei
 * Stunden nach.
 */
function clockOfDay(now: number): number {
  const parts = new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(now);

  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  return ((get('hour') % 24) * 3600 + get('minute') * 60 + get('second')) * 1000;
}
